'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculatorResult {
  monthlySavings: number;
  annualSavings: number;
  roi: number;
  breakEvenMonths: number;
}

const ROICalculator = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedService, setSelectedService] = useState<string>('aiAgents');
  const [teamSize, setTeamSize] = useState<number>(5);
  const [projectDuration, setProjectDuration] = useState<number>(6);
  const [currentCost, setCurrentCost] = useState<number>(50000);
  const [result, setResult] = useState<CalculatorResult | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services = [
    { id: 'aiAgents', label: 'AI Agents Development', savingsRate: 0.35 },
    { id: 'staffing', label: 'Contract Staffing', savingsRate: 0.25 },
    { id: 'dataEngineering', label: 'Data Engineering', savingsRate: 0.30 },
    { id: 'licensing', label: 'License Reselling', savingsRate: 0.15 },
    { id: 'copilot', label: 'Copilot Workshops', savingsRate: 0.40 },
  ];

  const calculateROI = () => {
    if (!isHydrated) return;

    const service = services.find((s) => s.id === selectedService);
    if (!service) return;

    const monthlyCost = currentCost / projectDuration;
    const monthlySavings = monthlyCost * service.savingsRate;
    const annualSavings = monthlySavings * 12;
    const totalSavings = monthlySavings * projectDuration;
    const roi = (totalSavings / currentCost) * 100;
    const breakEvenMonths = Math.ceil(currentCost / monthlySavings);

    setResult({
      monthlySavings,
      annualSavings,
      roi,
      breakEvenMonths,
    });
  };

  if (!isHydrated) {
    return (
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-card rounded-lg shadow-lg p-8 animate-pulse">
            <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-muted rounded w-1/2 mb-8"></div>
            <div className="space-y-4">
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            ROI Calculator
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Calculate potential savings and return on investment for our services
          </p>
        </div>

        <div className="bg-card rounded-lg shadow-lg p-6 md:p-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-headline font-semibold text-foreground mb-2">
                  Select Service
                </label>
                <select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  className="w-full px-4 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-headline font-semibold text-foreground mb-2">
                  Team Size: {teamSize} members
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={teamSize}
                  onChange={(e) => setTeamSize(Number(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-headline font-semibold text-foreground mb-2">
                  Project Duration: {projectDuration} months
                </label>
                <input
                  type="range"
                  min="1"
                  max="24"
                  value={projectDuration}
                  onChange={(e) => setProjectDuration(Number(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-headline font-semibold text-foreground mb-2">
                  Current Monthly Cost ($)
                </label>
                <input
                  type="number"
                  value={currentCost}
                  onChange={(e) => setCurrentCost(Number(e.target.value))}
                  className="w-full px-4 py-3 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="50000"
                />
              </div>

              <button
                onClick={calculateROI}
                className="w-full px-6 py-3 bg-conversion text-conversion-foreground font-headline font-semibold text-base rounded-md hover:bg-opacity-90 shadow-cta hover:shadow-partnership transition-all duration-base transform hover:-translate-y-0.5"
              >
                Calculate ROI
              </button>
            </div>

            <div className="bg-muted rounded-lg p-6">
              {result ? (
                <div className="space-y-6">
                  <div className="text-center pb-6 border-b border-border">
                    <p className="text-sm font-body text-muted-foreground mb-2">
                      Estimated ROI
                    </p>
                    <p className="text-4xl font-headline font-bold text-success">
                      {result.roi.toFixed(1)}%
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-card rounded-md">
                      <div className="flex items-center space-x-3">
                        <Icon
                          name="CurrencyDollarIcon"
                          size={24}
                          variant="solid"
                          className="text-primary"
                        />
                        <span className="font-body text-foreground">
                          Monthly Savings
                        </span>
                      </div>
                      <span className="font-headline font-bold text-foreground">
                        ${result.monthlySavings.toLocaleString()}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-card rounded-md">
                      <div className="flex items-center space-x-3">
                        <Icon
                          name="ChartBarIcon"
                          size={24}
                          variant="solid"
                          className="text-secondary"
                        />
                        <span className="font-body text-foreground">
                          Annual Savings
                        </span>
                      </div>
                      <span className="font-headline font-bold text-foreground">
                        ${result.annualSavings.toLocaleString()}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-card rounded-md">
                      <div className="flex items-center space-x-3">
                        <Icon
                          name="ClockIcon"
                          size={24}
                          variant="solid"
                          className="text-accent"
                        />
                        <span className="font-body text-foreground">
                          Break-even Period
                        </span>
                      </div>
                      <span className="font-headline font-bold text-foreground">
                        {result.breakEvenMonths} months
                      </span>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-border">
                    <p className="text-xs text-muted-foreground text-center">
                      *Estimates based on industry averages and typical project
                      parameters. Actual results may vary.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <Icon
                    name="CalculatorIcon"
                    size={48}
                    variant="outline"
                    className="text-muted-foreground mb-4"
                  />
                  <p className="text-muted-foreground font-body">
                    Enter your project details and click Calculate ROI to see
                    potential savings
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;